import type { autoColsItem } from "../dynamic-table";
import type { TableColumn } from "../types";
import type { TableState } from "@/components/lt-a-table/src";
import { i18n } from "../../../../locales/index";
import { getFixedCols, getLocalDate } from "./commonMethods";
import { createVNode } from "vue";
import type { Ref } from "vue";
import { message } from "ant-design-vue";
import { cloneDeep } from "lodash-es";
import uploadImage from "../components/upload-image.vue";
import { isNullAndUnDef } from "@/utils/is";
import dayjs from "dayjs";
import { commonSaveData, commonDelData } from "./getBaseDataFromServer";

type delDataStruct = {
  Ids: string[] | number[];
  Data: string[] | number[];
};

const setRequireHeader = (cols: autoColsItem) => {
  if (cols.required) {
    return createVNode(
      <span>
        <span style="color:red">*</span>
        <span>{cols.title}</span>
      </span>
    );
  } else {
    return cols.title;
  }
};

const getReadOnlyData = (data) => {
  return <span>{data}</span>;
};

const getEditByCondition = (record, col) => {
  let bRet = true;
  if (
    typeof col.ifCondition == "undefined" ||
    typeof col.editCondition == "undefined"
  ) {
    //只要有一个字段没配置，则认为不需要限制
    return bRet;
  }
  if (col.ifCondition == record[col.editCondition]) {
    //如果限制条件满足，则不允许编辑
    bRet = false;
  }
  //返回魏true的话，则是允许编辑
  return bRet;
};

const checkInputIfValid = (row, col, value) => {
  const { t } = i18n.global;
  //验证input输入的数据是否合格
  if (col.type == undefined || col.type == "" || col.type == null) {
    //如果不配置类型，则默认不进行验证
    return true;
  }
  switch (col.type) {
    case "int":
      {
        if (value == null || value == undefined || value == "") {
          return true; //允许输入为空
        }
        const bRetT = /^[0-9]*[1-9][0-9]*$/.test(value);
        const bRetL = checkIfLengthValid(col, value);
        if (bRetT == false) {
          message.warning(t("component.table.IntWarning", [col.Name]));
          return false;
        }
        if (bRetL == false) {
          message.warning(t("component.table.LengthWarning", [col.Name]));
          return false;
        }
        if (bRetT == true && bRetL == true) {
          return true;
        }
      }
      break;
    case "float":
      {
        //验证是否是正确的浮点数
        if (value == null || value == undefined || value == "") {
          return true; //允许输入为空
        }
        const bRetT = /^\d+(\.\d+)?$/.test(value);
        const bRetL = checkIfLengthValid(col, value);
        if (bRetT == false) {
          message.warning(t("component.table.FloatWarning", [col.Name]));
          return false;
        }
        if (bRetL == false) {
          message.warning(t("component.table.LengthWarning", [col.Name]));
          return false;
        }
        if (bRetT == true && bRetL == true) {
          return true;
        }
      }
      break;
    default: {
      const bRetL = checkIfLengthValid(col, value);
      if (bRetL == false) {
        message.warning(t("component.table.LengthWarning", [col.Name]));
        return false;
      }
    }
  }
};

const checkIfLengthValid = (col, value) => {
  //检查长度是否超长
  let sValue = "";
  if (typeof value == "number") {
    //如果是数字，则先转换成字符串
    sValue = value.toString();
  } else {
    sValue = value;
  }
  if (
    col.maxlength == undefined ||
    col.maxlength == null ||
    col.maxlength == ""
  ) {
    //不配置的情况下，则不判断长度是否超长
    return true;
  }
  if (col.maxlength >= sValue.length) {
    return true;
  } else {
    return false;
  }
};

const EditTextBlur = (record, emit) => {
  emit("edit-row-data-change", record);
};

const editTextBox = (record, col, emit) => {
  const key = col.dataIndex;
  const EditTextChange = () => {
    const bRet = checkInputIfValid(record, col, record[key]);
    if (bRet) {
      emit("check-data", record, true, key); //之所以要带true和false两个参数往外抛出，是为了方便二次开发人员做有效性的判断
    } else {
      emit("check-data", record, false, key); //
    }
  };

  return (
    <a-input
      allowClear
      size="small"
      autofocus={true}
      v-model:value={record[key]}
      maxlength={col.maxlength == undefined ? 500 : col.maxlength}
      onInput={EditTextChange}
      onblur={EditTextBlur(record, emit)}
    ></a-input>
  );
};
//向外抛出click事件
const TextBoxPop = (record, col, emit) => {
  const EditTextClick = () => {
    emit("text-click", record, col);
  };
  return (
    <span onClick={EditTextClick}>
      <em
        style="float: right;color:green"
        class={col.icon ? col.icon : "icon-search green"}
      ></em>
    </span>
  );
};

const _getComTextLabel = (props, record, item, emit) => {
  const editByCondition = getEditByCondition(record, item); //
  if (!props.isAutoEditable) {
    return getReadOnlyData(record[item.dataIndex]);
  } else {
    if (!item.defaultEditable) {
      //不可编辑的时候，生成固定文本
      return getReadOnlyData(record[item.dataIndex]);
    } else {
      if (editByCondition) {
        //如果可编辑，但是readonly为true ,说明需要生成带图标控件，需要弹窗
        if (!item.readonly) {
          return editTextBox(record, item, emit);
        } else {
          return TextBoxPop(record, item, emit);
        }
      } else {
        return getReadOnlyData(record[item.dataIndex]);
      }
    }
  }
};

const _getComBoxlabel = (props, record, col) => {
  const editByCondition = getEditByCondition(record, col); //
  if (typeof col.Option == "undefined") {
    return <span>{record[col.dataIndex]}</span>;
  } else if (
    props.isAutoEditable == false ||
    !col.defaultEditable ||
    editByCondition == false
  ) {
    let showLabel = "";
    const targetItem = col.Option.filter((item) => {
      return item.value == record[col.dataIndex];
    });
    if (targetItem.length > 0) {
      showLabel = targetItem[0].label;
    }
    return <span>{showLabel}</span>;
  } else {
    return editComBox(record, col);
  }
};

const filterOption = (input: string, option: any) => {
  if (option.label) {
    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  } else {
    return option.label.indexOf(input) >= 0;
  }
};

const editComBox = (record, col) => {
  const key = col.dataIndex;
  return (
    <a-select
      style="width:100%"
      size="small"
      placeholder=""
      mode={col.Mode ? col.Mode : null}
      allowClear
      show-search
      v-model:value={record[key]}
      filterOption={filterOption}
      maxTagCount={col.maxTagCount ? col.maxTagCount : null}
      onChange={(data) => {
        record[key] = data;
      }}
    >
      {colGetComboxOptions(col, col.Option)}
    </a-select>
  );
};

const colGetComboxOptions = (col, Option) => {
  return Option.map((item) => {
    return (
      <a-select-option value={item.value} label={item.label}>
        {item.label}
      </a-select-option>
    );
  });
};

const getCheckBox = (props, record, col) => {
  if (!props.isAutoEditable) {
    return (
      <a-checkbox checked={record[col.dataIndex]} disabled={true}></a-checkbox>
    );
  } else {
    if (!col.defaultEditable) {
      return (
        <a-checkbox
          checked={record[col.dataIndex]}
          disabled={true}
        ></a-checkbox>
      );
    } else {
      return editCheckBox(record, col);
    }
  }
};

const editCheckBox = (record, col) => {
  const key = col.dataIndex;
  return <a-checkbox v-model:checked={record[key]} />;
};

const getfieldNames = function (col: autoColsItem) {
  if (typeof col.fieldNames != "undefined") {
    return col.fieldNames;
  } else {
    return { children: "children", label: "title", value: "value" };
  }
};

const getTreeSelect = function (col: autoColsItem, record: any, props) {
  if (col.defaultEditable && props.isAutoEditable) {
    const key = col.dataIndex;
    return (
      <a-tree-select
        value={record[key]}
        size="small"
        treeData={col.Option}
        allowClear={true}
        fieldNames={getfieldNames(col)}
        onChange={(data) => {
          record[key] = data;
        }}
      ></a-tree-select>
    );
  } else {
    return _getTreeLabel(record, col);
  }
};

//生成树形表格
const _getTreeLabel = (record: any, col: autoColsItem) => {
  const showLabel = getShowName(record, col);
  return getReadOnlyData(showLabel);
};

const getShowName = (record: any, col: autoColsItem) => {
  let label = "";
  if (record[col?.dataIndex || col.dataIndex]) {
    col.fieldNames = col?.fieldNames || {
      label: "label",
      value: "value",
      children: "children",
    };
    let treeData = cloneDeep(col?.Option || []);
    while (treeData.length > 0) {
      const temp = treeData.shift();
      if (
        record[col.dataIndex || col.dataIndex] === temp[col.fieldNames.value]
      ) {
        label = temp[col?.fieldNames.label];
        break;
      } else {
        if (
          temp[col.fieldNames.children] &&
          temp[col.fieldNames.children].length != 0
        ) {
          treeData = treeData.concat(temp[col.fieldNames.children]);
        }
      }
    }
  }
  return label;
};

const showColorComponent = function (record: any, col: autoColsItem) {
  const field = col.dataIndex;
  return <a-input disabled={true}  value={record[field]} type="color" onChange={(data) => {record[field] = data;
            }}></a-input>;
  
};

const showTimeBox = function (record: any, col: autoColsItem, props: any) {
  if (col.defaultEditable && props.isAutoEditable) {
    const key = col.dataIndex;
    return (
      <a-date-picker
        value={record[key]}
        valueFormat={
          col.valueFormat
            ? col.valueFormat
            : col.showTime
            ? "YYYY-MM-DD HH:mm:ss"
            : "YYYY-MM-DD"
        }
        onChange={(data) => {
          record[key] = data;
        }}
        size="small"
      ></a-date-picker>
    );
  } else {
    if (isNullAndUnDef(record[col.dataIndex])) {
      return "";
    } else {
      return showTimeLabel(record, col);
    }
  }
};

const showTimeLabel = function (record: any, col: autoColsItem) {
  const field = col.dataIndex;
  return getLocalDate(record[field], col.showTime);
};

const setOperatrBtn = (record, col, emit) => {
  const btnClick = (key) => {
    //key的作用是用来识别是哪个按钮事件
    emit("on-btn-click", record, col, key);
  };

  return (
    <span>
      {col.btnOption.map((items) => {
        const color = items.color ? items.color : "dodgerblue";
        const style = `color:${color};cursor:pointer`;
        //生成下拉选项
        const btnKey = items.btnKey;
        return (
          <a-button
            size="small"
            type={items.type}
            title={items.tooltip}
            label={items.label}
            disabled={items.disabled}
            style={style}
            onClick={
              items.ClickFun
                ? items.ClickFun.bind(this, { record, col, btnKey })
                : btnClick.bind(this, items.btnKey)
            }
          >
            {items.icon ? <em class={items.icon}></em> : ""}
            {items.label ? <label>{items.label}</label> : ""}
          </a-button>
        );
      })}
    </span>
  );
};

//20250724调整可编辑列的生成时机，不管是可编辑还是非可编辑列，全部通过参数去生成
const getEditBtns = function (
  columns,
  props,
  spinning,
  emit,
  tableAction,
  record
) {
  //根据配置文件获取操作列的配置信息
  const key = props.rowKey as string;
  const { isEditable, cancelEditable, startEditable, reload } = tableAction;
  const rowEditAble = isEditable(record[key]);
  const isShowSave = props.editColOperate.isShowSaveBtn;
  const isShowEdit = props.editColOperate.isShowEditBtn;
  const isShowCancel = props.editColOperate.isShowCancelBtn;
  const isShowDelete = props.editColOperate.isShowDeleteBtn;
  const editTitle = props.editColOperate.editBtnLabel;

  const handleSaveBtnClick = async function () {
    const { cancelEditable, validateRow, reload } = tableAction;
    const key = props.rowKey as string;
    const result = await validateRow(record[key]);
    let sendData = result;
    let bRet = false;
    if (typeof props.editColOperate.formatShowSaveFun != "undefined"&&props.editColOperate.formatShowSaveFun!=""&&props.editColOperate.formatShowSaveFun!=null) {
      sendData = props.editColOperate.formatShowSaveFun(result);
    }
    if (typeof props.editColOperate.saveUrl!="undefined"&&props.editColOperate.saveUrl!=""&&props.editColOperate.saveUrl!=null) {
      if (typeof spinning != "undefined") {
        spinning.value = true;
      }
      bRet = await commonSaveData(props.editColOperate.saveUrl, sendData);
      if (typeof spinning != "undefined") {
        spinning.value = false;
      }
      if (bRet) {
        reload();
      }
      cancelEditable(record[key]);
    } else {
      cancelEditable(record[key]);
    }
    emit("save-edit-row", result, bRet);
  };

  const handleCancelEdit=function(){
    cancelEditable(record[key]);
  }

  const handleDelete=async function(){
    let sendData = { Data: record } as unknown as delDataStruct;
    if (typeof props.editColOperate.formatDelDataFun != "undefined"&&props.editColOperate.formatDelDataFun!=""&&props.editColOperate.formatDelDataFun!=null) {
        sendData = props.editColOperate.formatDelDataFun([record[key]]);
    }
    if (typeof spinning != "undefined") spinning.value = true;
      let bRet = false;
      if (typeof props.editColOperate.DelUrl!="undefined"&&props.editColOperate.DelUrl!=""&&props.editColOperate.DelUrl!=null) {
        bRet = await commonDelData(props.editColOperate.DelUrl,sendData);
      }
      emit("delete-row", record, bRet);
      reload();
      if (typeof spinning != "undefined") {
        spinning.value = false;
      }
  }

  const handleEdit=function(){
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].Style.toLowerCase() == "datetime") {
        const field = columns[i].Field? columns[i].Field: columns[i].dataIndex;
        if (record[field]) {
          record[field] = dayjs(record[field]);
        } else {
          record[field] = "";
        }
      }
    }
    startEditable(record[key], record);
  }

  if (rowEditAble == true) {
    return (
      <span>
        {isShowSave ? (
          <a-button type="text" size="small" style="color:#1E90FF;cursor:pointer" onClick={handleSaveBtnClick}>
            {props.editColOperate.saveBtnLabel}
          </a-button>
        ) : (
          ""
        )}
        {isShowCancel ? (
          <a-button type="text" size="small" style="color:#1E90FF;cursor:pointer" onClick={handleCancelEdit}>
            {props.editColOperate.cancelBtnLabel}
          </a-button>
        ) : (
          ""
        )}
        {isShowDelete ? (
          <a-button type="text" size="small" style="color:#1E90FF;cursor:pointer" onClick={handleDelete}>
            {props.editColOperate.deleteBtnLabel}
          </a-button>
        ) : (
          ""
        )}
      </span>
    );
  } else {
    return (
      <span>
        {isShowEdit ? (
          <a-button type="text" style="color:#1E90FF;cursor:pointer" size="small" onClick={handleEdit}>
            {editTitle}
          </a-button>
        ) : (
          ""
        )}
        {isShowDelete ? (
          <a-button type="text" style="color:#1E90FF;cursor:pointer" size="small"
            onClick={handleDelete}>{props.editColOperate.deleteBtnLabel}</a-button>
        ) : (
          ""
        )}
      </span>
    );
  }
};

export const formatAutoCols = (
  props,
  emit,
  state?: TableState,
  col?: autoColsItem[] | TableColumn[],
  spinning?: Ref<boolean>,
  tableAction?: any
) => {
  const { t } = i18n.global;
  const innerCols = [] as TableColumn[];
  let targetCols = [] as any[];
  if (typeof col != "undefined") {
    targetCols = col.filter((item) => {
      return item.Visible == true;
    });
  } else {
    targetCols = props.columns?.filter((item) => {
      return item.Visible == true;
    });
  }
  targetCols = getFixedCols(targetCols);
  if (targetCols?.length == 0) {
    return innerCols;
  }

  if (targetCols?.length && targetCols.length > 0) {
    for (let i = 0; i < targetCols.length; i++) {
      const tempItem = {} as TableColumn;
      tempItem.title = setRequireHeader(targetCols[i]);
      tempItem.dataIndex = targetCols[i].dataIndex;
      tempItem.width = targetCols[i].width ? targetCols[i].width : NaN;
      tempItem.ellipsis = true;
      tempItem.resizable =
        typeof targetCols[i].resizable == "undefined" ||
        targetCols[i].resizable == true
          ? true
          : false;
      tempItem.fixed = targetCols[i].fixed ? targetCols[i].fixed : false;
      tempItem.hideInSearch = targetCols[i].ifshowInQueryForm ? false : true;
      tempItem.sorter = targetCols[i].sorter ? targetCols[i].sorter : false;
      //tempItem.Style = targetCols[i].Style?targetCols[i].Style:"";
      tempItem.editable = targetCols[i].editable
        ? targetCols[i].editable
        : false;
      tempItem.defaultEditable = targetCols[i].defaultEditable
        ? targetCols[i].defaultEditable
        : false;
      tempItem.align = targetCols[i].align ? targetCols[i].align : "left";
      tempItem.customRender = ({ record }) => {
        return <span>{record[targetCols[i].dataIndex]}</span>;
      };
      const style =
        typeof targetCols[i].Style == "undefined"
          ? "textbox"
          : targetCols[i].Style.toLowerCase();
      if (style == "textbox") {
        tempItem.formItemProps = {
          rules: targetCols[i].required
            ? [{ required: true, message: t("component.table.Input") }]
            : [],
          componentProps: ({ formInstance, formModel }: any) => ({
            component: "Input",
            size: "small",
            readonly: !targetCols[i].editable,
            "allow-clear": true,
            bordered: !targetCols[i].editable,
          }),
        };
        tempItem.customRender = ({ record }) => {
          return _getComTextLabel(props, record, targetCols[i], emit);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "textarea") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "InputTextArea",
            rules: targetCols[i].required
              ? [
                  {
                    required: true,
                    message: "请选择",
                    type: targetCols[i].dataType
                      ? targetCols[i].dataType.toLowerCase() == "int"
                        ? "number"
                        : "string"
                      : "string",
                  },
                ]
              : [],
            componentProps: ({ tableInstance, formModel }) => ({
              size: "small",
              readOnly: !targetCols[i].editable,
              "allow-clear": true,
              bordered: !targetCols[i].editable,
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          const key = targetCols[i].dataIndex;
          if (targetCols[i].defaultEditable) {
            return (
              <a-textarea
                value={record[key]}
                onChange={(data) => {
                  record[key] = data;
                }}
              ></a-textarea>
            );
          } else {
            return <span>{record[key]} </span>;
          }
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "combox") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "Select",
            rules: targetCols[i].required
              ? [
                  {
                    required: true,
                    message: "请选择",
                    type: targetCols[i].dataType
                      ? targetCols[i].dataType.toLowerCase() == "int"
                        ? "number"
                        : "string"
                      : "string",
                  },
                ]
              : [],
            componentProps: (Option) => ({
              options: targetCols[i].Option,
              size: "small",
              allowClear: targetCols[i].editable
                ? targetCols[i].allowClear
                  ? targetCols[i].allowClear
                  : true
                : false,
              showSearch: true,
              mode: targetCols[i].Mode ? targetCols[i].Mode : null,
              maxTagCount: targetCols[i].maxTagCount
                ? targetCols[i].maxTagCount
                : null,
              filterOption: filterOption,
              onChange(value) {
                const { formModel } = Option;
                formModel[targetCols[i].dataIndex] = value;
                emit("select-change", formModel, targetCols[i]);
              },
              disabled: !targetCols[i].editable,
              bordered: targetCols[i].editable,
              showArrow: targetCols[i].editable,
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          return _getComBoxlabel(props, record, targetCols[i]);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "checkbox") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "Checkbox",
            componentProps: (Option) => ({
              disabled: !targetCols[i].editable,
              onChange(value) {
                const { formModel } = Option;
                formModel[targetCols[i].dataIndex] = value.target.checked;
              },
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          return getCheckBox(props, record, targetCols[i]);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "tree" || style == "selecttree") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "TreeSelect",
            rules: targetCols[i].rules
              ? targetCols[i].rules
              : targetCols[i].required
              ? [
                  {
                    required: true,
                    message: "请选择",
                    type: targetCols[i].dataType
                      ? targetCols[i].dataType.toLowerCase() == "int"
                        ? "number"
                        : "string"
                      : "string",
                  },
                ]
              : [],
            componentProps: (Option) => ({
              size: "small",
              treeData: targetCols[i].Option,
              allowClear: true,
              onChange(value) {
                const { formModel } = Option;
                formModel[targetCols[i].dataIndex] = value;
              },
              disabled: !targetCols[i].editable,
              fieldNames: getfieldNames(targetCols[i]),
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          return getTreeSelect(targetCols[i], record, props);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "color") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "Input",
            //@ts-ignore
            readonly: !targetCols[i].editable,
            type: "color",
            size:"small",
            "allow-clear": true,
            bordered: !targetCols[i].editable,
            componentProps: ({ formInstance, formModel, tableRowKey }) => ({
              color: formModel[targetCols[i].dataIndex],
              onColorChange: (hex: string, rgba: string) => {
                formModel[targetCols[i].dataIndex] = hex;
              },
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          return showColorComponent(record, targetCols[i]);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "datetime") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: "DatePicker",
            required: targetCols[i].rules
              ? targetCols[i].rules
              : targetCols[i].required
              ? targetCols[i].required
              : false,
            componentProps: ({ tableInstance, formModel }) => ({
              disabled: !targetCols[i].editable,
              valueFormat: targetCols[i].valueFormat
                ? targetCols[i].valueFormat
                : targetCols[i].showTime
                ? "YYYY-MM-DD HH:mm:ss"
                : "YYYY-MM-DD",
              rules: targetCols[i].isRequire
                ? [
                    {
                      required: true,
                      message: "请选择",
                    },
                  ]
                : [],
              onChange(value) {
                formModel[targetCols[i].dataIndex] = value;
                console.log("tableInstance", tableInstance?.reload());
              },
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          return showTimeBox(record, targetCols[i], props);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "image") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            component: (schema) => {
              return createVNode(uploadImage, {
                maxCount: targetCols[i].maxCount ? targetCols[i].maxCount : 1,
                disabled: !targetCols[i].editable,
                onFileChange: (files: any) => {
                  const { formModel } = schema;
                  formModel[targetCols[i].dataIndex] = files;
                },
              });
            },
          };
        }
        tempItem.customRender = ({ record }) => {
          let stringHeader = "data:image/png;base64,";
          if (targetCols[i].imageType) {
            if (targetCols[i].imageType.toLocaleLowerCase() == "jpg") {
              stringHeader = "data:image/image/jpeg;base64,";
            }
          }
          return (
            <a-image
              width={22}
              src={stringHeader + record[targetCols[i].dataIndex]}
            ></a-image>
          );
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (style == "number") {
        if (targetCols[i].ifshowInQueryForm) {
          tempItem.formItemProps = {
            componentProps: ({ formInstance, formModel }: any) => ({
              component: "InputNumber", //formatLabelShow(formInstance, formModel, cols[i]),
              rules: targetCols[i].rules
                ? targetCols[i].rules
                : targetCols[i].isRequire
                ? [
                    {
                      required: true,
                      message: "请输入",
                    },
                  ]
                : [],
              size: "small",
              readonly: !targetCols[i].editable,
            }),
          };
        }
        tempItem.customRender = ({ record }) => {
          if (targetCols[i].defaultEditable) {
            const key = targetCols[i].dataIndex;
            return (
              <a-input-number
                value={record[key]}
                onChange={(data) => {
                  record[key] = data;
                }}
              ></a-input-number>
            );
          } else {
            const field = targetCols[i].dataIndex;
            return <span>{record[field]} </span>;
          }
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
        //20250723将action关键字改成operate
      } else if (targetCols[i].Style.toLowerCase() == "operate") {
        tempItem.align = "center";
        tempItem.customRender = ({ record }) => {
          return setOperatrBtn(record, targetCols[i], emit);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      } else if (targetCols[i].dataIndex) {
        if (targetCols[i].dataIndex.toLowerCase() == "index") {
          innerCols.push(targetCols[i]);
        }
      } else {
        tempItem.formItemProps = {
          rules: targetCols[i].required
            ? [{ required: true, message: t("component.table.Input") }]
            : [],
          componentProps: ({ formInstance, formModel }: any) => ({
            component: "Input",
            size: "small",
            readonly: !targetCols[i].editable,
            "allow-clear": true,
            bordered: !targetCols[i].editable,
          }),
        };
        tempItem.customRender = ({ record }) => {
          return _getComTextLabel(props, record, targetCols[i], emit);
        };
        const copy = {} as TableColumn;
        Object.assign(copy, tempItem);
        innerCols.push(copy);
      }
    }
    //  20250723增加编辑相关参数
    if (props.editColOperate.isShowEditCol == true) {
      const actionItem = {
        title: props.editColOperate.editOperateTitle,
        align: props.editColOperate.editOperatealign,
        width: props.editColOperate.editOperatewidth,
        dataIndex: "ACTION",
        fixed: props.editColOperate.editOperatefixed,
        customRender: ({ record }) => {
          return getEditBtns(
            targetCols,
            props,
            spinning,
            emit,
            tableAction,
            record
          );
        },
      };
      const copy = {} as TableColumn;
      Object.assign(copy, actionItem);
      innerCols.push(copy);
    }
  }
  return innerCols;
};
